import axios from './HTTPClient';

import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

const DEFAULT_API_VERSION = import.meta.env.VITE_API_VERSION as string;
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string;

interface ApiClientOptions {
  apiVersion?: string;
}

class ApiClient {
  private apiVersion: string;
  private resource: string;
  private options: ApiClientOptions;
  private client: AxiosInstance;

  constructor(resource: string, options: ApiClientOptions = {}) {
    this.apiVersion = options.apiVersion || DEFAULT_API_VERSION;
    this.resource = resource;
    this.options = options;
    this.client = axios;
  }

  private get url(): string {
    return `${API_BASE_URL}/${this.apiVersion}/${this.resource}`;
  }

  public get<T = any>(options: AxiosRequestConfig = {}, resource = ''): Promise<AxiosResponse<T>> {
    const handledResource = resource ? `/${resource}` : '';
    return this.client.get<T>(`${this.url}${handledResource}`, options);
  }

  public show<T = any>(id: string | number, options: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> {
    return this.client.get<T>(`${this.url}/${id}`, options);
  }

  public create<T = any>(
    data: any = {},
    resource = '',
    headers: Record<string, string> = {},
  ): Promise<AxiosResponse<T>> {
    const handledResource = resource ? `/${resource}` : '';
    return this.client.post<T>(`${this.url}${handledResource}`, data, { headers });
  }

  public updateWithPatch<T = any>(
    data: any = {},
    resource = '',
    headers: Record<string, string> = {},
  ): Promise<AxiosResponse<T>> {
    const handledResource = resource ? `/${resource}` : '';
    return this.client.patch<T>(`${this.url}${handledResource}`, data, { headers });
  }

  public update<T = any>(
    id: string | number,
    data: any,
    headers: Record<string, string> = {},
  ): Promise<AxiosResponse<T>> {
    return this.client.put<T>(`${this.url}/${id}`, data, { headers });
  }

  public delete<T = any>(
    id: string | number,
    resource = '',
    headers: Record<string, string> = {},
  ): Promise<AxiosResponse<T>> {
    const handledResource = resource ? `/${resource}` : '';
    return this.client.delete<T>(`${this.url}${handledResource}/${id}`, { headers });
  }
}

export default ApiClient;
